import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, FileText, Check, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";

const inputClass = "h-12 rounded-[var(--radius-sm)] border-[color:var(--bp-border)] bg-[color:var(--bp-bg-2)] text-[color:var(--bp-cream)] placeholder:text-[color:var(--bp-text-muted)]/60";
const labelClass = "text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bp-text-muted)]";

export const CvAssessment = () => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", market: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error("Please enter your email."); return; }
    if (!form.consent) { toast.error("Please confirm your consent."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("email", form.email);
      fd.append("name", form.name);
      fd.append("market", form.market);
      fd.append("consent", String(form.consent));
      if (file) fd.append("file", file);
      await api.createCvLead(fd);
      setDone(true);
      toast.success("Received in confidence", { description: "We'll review and be in touch discreetly." });
    } catch (err) { toast.error("Upload failed. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="rounded-[var(--radius-lg)] border border-[color:var(--bp-success)]/40 bg-[color:var(--bp-surface)] p-8 text-center" data-testid="cv-assessment-done">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[color:var(--bp-success)]/50 text-[color:var(--bp-success)]"><Check className="h-6 w-6" /></span>
        <h3 className="mt-5 font-display text-xl font-semibold text-[color:var(--bp-cream)]">Your CV has been received</h3>
        <p className="mt-2 text-[14px] text-[color:var(--bp-text-muted)]">In strict confidence. We&apos;ll review and reach out when there&apos;s a relevant fit.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-7 sm:p-8" data-testid="cv-assessment-form">
      <h3 className="font-display text-xl font-semibold text-[color:var(--bp-cream)]">Confidential CV assessment</h3>
      <p className="mt-2 text-[14px] text-[color:var(--bp-text-muted)]">Senior bankers: submit your CV for a discreet, no-obligation market read.</p>

      <button type="button" onClick={() => fileRef.current?.click()} data-testid="cv-upload-button"
        className="mt-6 flex w-full flex-col items-center justify-center gap-2 rounded-[var(--radius-md)] border border-dashed border-[color:var(--bp-border)] bg-[color:var(--bp-bg-2)] px-5 py-8 text-center transition-colors hover:border-[color:var(--bp-teal)]">
        {file ? <FileText className="h-7 w-7 text-[color:var(--bp-teal)]" /> : <UploadCloud className="h-7 w-7 text-[color:var(--bp-text-muted)]" />}
        <span className="text-[13.5px] text-[color:var(--bp-cream)]">{file ? file.name : "Click to upload your CV (PDF, DOCX)"}</span>
        <span className="text-[11px] text-[color:var(--bp-text-muted)]">Max 10MB · 100% confidential</span>
      </button>
      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="cv-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label className={labelClass}>Name</Label><Input data-testid="cv-name" className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" /></div>
        <div className="space-y-2"><Label className={labelClass}>Email</Label><Input data-testid="cv-email" type="email" className={inputClass} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></div>
      </div>
      <label className="mt-5 flex cursor-pointer items-start gap-3 text-[13px] text-[color:var(--bp-text-muted)]">
        <Checkbox data-testid="cv-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5 border-[color:var(--bp-border)] data-[state=checked]:bg-[color:var(--bp-teal)] data-[state=checked]:border-[color:var(--bp-teal)]" />
        <span>I consent to a confidential review of my CV by Beyond Potentials.</span>
      </label>
      <button type="submit" disabled={loading} data-testid="cv-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[color:var(--bp-teal)] px-6 py-4 text-sm font-semibold text-[color:var(--bp-bg)] transition-shadow hover:shadow-[var(--shadow-lift)] disabled:opacity-60">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {loading ? "Submitting…" : "Submit confidentially"}
      </button>
    </form>
  );
};
